import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import './smartTooltip';

export const getInitials = (email) => {
  if (!email) return '??';
  const parts = email.split('@')[0].split('.');
  if (parts.length > 1) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return parts[0].substring(0, 2).toUpperCase();
};

export const getRandomColor = (email) => {
  const colors = ['#0A2A91', '#4834B7', '#7E6FD7', '#2C78E4', '#9C3FBA', '#5B21D6', '#1F4D99', '#8A4FFF', '#B641A8', '#2E97E6'];
  
  const hash = email.split('').reduce((acc, char) => {
    return char.charCodeAt(0) + acc;
  }, 0);
  
  return colors[hash % colors.length];
};

// UserCircleDisplay component to show email initials in circles
export const UserCircleDisplay = ({
  users,
  maxDisplay = 4,
  circleSizeClass = "default",
  projectUsers = null,
  ownerEmail = null
}) => {
  const [showAllUsers, setShowAllUsers] = useState(false);

  const toggleAllUsers = () => {
    setShowAllUsers(!showAllUsers);
  };

  const grouped = Array.isArray(projectUsers);
  const normalizedOwnerEmail = ownerEmail?.trim().toLowerCase();
  const ownerUser = normalizedOwnerEmail ? { email: ownerEmail.trim() } : null;
  const isOwner = (user) => user.email?.trim().toLowerCase() === normalizedOwnerEmail;
  const explicitUsers = users.filter(user => !isOwner(user));
  const linkedProjectUsers = grouped ? projectUsers.filter(user => !isOwner(user)) : [];
  const allUsers = grouped
    ? [...(ownerUser ? [ownerUser] : []), ...explicitUsers, ...linkedProjectUsers]
    : [...(ownerUser ? [ownerUser] : []), ...explicitUsers];

  // With the lists split, each avatar's tooltip also says how that user got
  // access — otherwise, with four or fewer (no "+N"), there was no way to
  // tell. Explicit users come first in allUsers, so the index is enough.
  const avatarTooltip = (user, idx) => {
    if (isOwner(user)) return `${user.email} · Owner`;
    if (!grouped) return user.email;
    const explicitUserCount = explicitUsers.length + (ownerUser ? 1 : 0);
    return `${user.email} · ${idx < explicitUserCount ? 'Explicitly added' : 'Access via project'}`;
  };

  // The avatars open the same grouped list as "+N" does, however few there
  // are — one place to see who was added and who comes in through a project,
  // and the only way to get at it without hover (touch screens).
  const openOnKey = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setShowAllUsers(true);
    }
  };

  const renderPopupUser = (user, idx) => (
    <div key={idx} className="col-6 mb-3">
      <div className="list-group-item d-flex align-items-center">
        <div
          className="more-circle more-circle--default d-flex align-items-center justify-content-center text-white fw-bold me-3"
          style={{ backgroundColor: getRandomColor(user.email) }}
        >
          {getInitials(user.email)}
        </div>
        <span className="text-truncate ev-small-p-dark">{user.email}</span>
      </div>
    </div>
  );

  // A later group gets extra space above its heading, so the two lists read
  // as separate groups rather than one run of names.
  const renderPopupGroup = (title, groupUsers, isFirst = true) => groupUsers.length > 0 && (
    <>
      <p className={`col-12 ev-reg-p-blue mb-2 ${isFirst ? '' : 'mt-2'}`}>{title}</p>
      {groupUsers.map((user, idx) => renderPopupUser(user, idx))}
    </>
  );

  return (
    <>
      <div className="d-flex align-items-center">
        {allUsers.slice(0, maxDisplay).map((user, idx) => (
          <div 
            key={idx} 
            className={`user-circle user-circle--${circleSizeClass} d-flex align-items-center justify-content-center text-white fw-bold smart-tooltip`}
            style={{ backgroundColor: getRandomColor(user.email), cursor: 'pointer' }}
            data-tooltip={avatarTooltip(user, idx)}
            role="button"
            tabIndex={0}
            aria-label={`${avatarTooltip(user, idx)}. Show all members`}
            onClick={() => setShowAllUsers(true)}
            onKeyDown={openOnKey}
          >
            {getInitials(user.email)}
          </div>
        ))}
        
        {allUsers.length > maxDisplay && (
          <div
            className={`more-circle more-circle--default d-flex align-items-center justify-content-center ev-btn-blue-outline-round ev-bold-reg smart-tooltip`}
            onClick={toggleAllUsers}
            data-tooltip="View all members"
          >
            +{allUsers.length - maxDisplay}
          </div>
        )}
      </div>
      
      {/* All users popup. Header (title + close) sits outside the scrolling
          area in its own flex-shrink-0 row, so a long member list scrolls
          without ever carrying the close button off screen with it.
          ev-card-2 (var(--background1), a plain opaque RGB) for the card
          background — ev-card-7's --color12 carries its own alpha channel,
          same problem as blur-background: the page underneath still shows
          through the names. */}
      {showAllUsers && ReactDOM.createPortal(
        <div className="position-fixed d-flex align-items-center justify-content-center modal fade show d-block bg-dark bg-opacity-50" style={{ zIndex: "var(--num-1000000000)" }}>
          {/* As tall as its members need, up to 60vh, then the list scrolls.
              A fixed height left a short list spread out over empty space. */}
          <div className="ev-card-2 p-4 col-5 d-flex flex-column" style={{ maxHeight: 'var(--vh-60)' }}>
            <div className="d-flex justify-content-between align-items-center mb-3 flex-shrink-0">
              <p className="ev-med-p-blue mb-0">All Members</p>
              <button className="ev-btn-text-only-fill ev-btn-large" onClick={toggleAllUsers}>
                <i className="bi bi-x-circle"></i>
              </button>
            </div>

            {/* scrollable-element's own height (vh-60) would otherwise fight
                flex-grow-1 for how tall this gets — height:auto lets the flex
                column decide, min-height:0 is what lets a flex child shrink
                below its content and actually scroll instead of overflowing
                the card. align-content-start keeps the wrapped rows packed at
                the top — a flex row's lines otherwise stretch to fill it. */}
            <div className="row scrollable-element flex-grow-1 align-content-start" style={{ height: 'auto', minHeight: 0 }}>
              {grouped ? (
                <>
                  {renderPopupGroup('Owner', ownerUser ? [ownerUser] : [])}
                  {renderPopupGroup('Explicitly added', explicitUsers, !ownerUser)}
                  {renderPopupGroup(
                    'Access via project',
                    linkedProjectUsers,
                    !ownerUser && explicitUsers.length === 0
                  )}
                </>
              ) : (
                <>
                  {ownerUser && renderPopupUser(ownerUser, 'owner')}
                  {explicitUsers.map((user, idx) => renderPopupUser(user, idx))}
                </>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};